import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useForm } from 'react-hook-form';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';

export type EInvoiceGranularity = 'TOTALS' | 'PRESENTATION' | 'DETAILED';

interface GranularityOption {
    id: EInvoiceGranularity;
    label: string;
}

interface DownloadEInvoiceForm {
    granularity: EInvoiceGranularity;
    includePdf: boolean;
}

interface Props {
    onClose: () => void;
    onDownload: (granularity: EInvoiceGranularity, includePdf: boolean) => Promise<unknown>;
}

const DownloadEInvoiceModal = ({ onClose, onDownload }: Props): JSX.Element => {
    const { control, handleSubmit, setValue } = useForm<DownloadEInvoiceForm>({
        defaultValues: {
            granularity: 'PRESENTATION',
            includePdf: true,
        },
    });

    const granularityOptions: GranularityOption[] = [
        { id: 'TOTALS', label: t('invoices.e-invoice-download.granularity-totals') },
        { id: 'PRESENTATION', label: t('invoices.e-invoice-download.granularity-presentation') },
        { id: 'DETAILED', label: t('invoices.e-invoice-download.granularity-detailed') },
    ];

    const onSave = () => {
        handleSubmit(async (data) => {
            try {
                await onDownload(data.granularity, data.includePdf);
                onClose();
            } catch (error) {
                logError(error);
                errorAlert(t('invoices.e-invoice-download.error'));
            }
        })();
    };

    return (
        <>
            <ModalHeader kit title={t('invoices.e-invoice-download.title')} onCancel={onClose} />
            <div className="p-5">
                <div className="flex flex-col gap-4">
                    <RadioInput
                        name="granularity"
                        control={control}
                        setValue={setValue}
                        label="invoices.e-invoice-download.granularity"
                        options={granularityOptions}
                        getOptionIdentifier="id"
                        getOptionLabel="label"
                    />
                    <CheckboxInput
                        name="includePdf"
                        control={control}
                        label="invoices.e-invoice-download.include-pdf"
                    />
                </div>
            </div>
            <ModalFooter
                kit
                onCancel={onClose}
                onSave={onSave}
                saveButtonTextId="invoices.e-invoice-download.download"
            />
        </>
    );
};

export default DownloadEInvoiceModal;
